import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Select } from '@nyce/admin-kit/ui';
import { EmptyState, Loading, Page, useAsync, useInterval } from '../../components/common';
import { loadTargets, useTargets } from '../../identity/session';
import { stats, AUTH_MODES, type AuthMode } from '../../api/stats';
import { SCOPES } from '../../api';
import { formatRelative } from '../../lib/format';
import { orgPath } from '../../pages/statistics/shared';
import { NewOrganizationDialog } from '../../pages/statistics/NewOrganization';
import { targetOf, useOnboardingConfig } from '../api/config';
import { addAlias, aliasOwner } from '../api/auth';
import { allCustomers } from '../api/directory';
import { workServerFiles } from '../generate/workserver';
import { Wizard, type WizardStep } from '../wizard/Wizard';
import { FileOutput } from '../wizard/FileOutput';
import { useWizardParam } from '../wizard/params';
import { PLACEHOLDER, rememberSecret, useSecrets } from '../wizard/secrets';
import { Facts, NeedsScope, NeedsSession, Note, SecretValue, StepActions, useAction } from '../wizard/parts';

/** W4: a datacollector organization, its link to AUTH, the ingest credential and the WorkServer's collector options. */
export function StatisticsWizardPage() {
  const config = useOnboardingConfig();
  const [ready, setReady] = useState(false);
  useEffect(() => {
    void loadTargets().then(() => setReady(true));
  }, []);
  return (
    <Page
      title="Statistics"
      description="Get an installation's logs, performance and metrics into the datacollector: the organization, its link to the AUTH customer, the ingest credential and the WorkServer's options — then wait for the first data."
    >
      {!config || !ready ? (
        <Loading loading />
      ) : !config.hubTarget ? (
        <EmptyState
          card
          title="The hub's own AUTH is not among the AuthTargets."
          hint="Statistics link to it."
        />
      ) : (
        <StatisticsWizard target={config.hubTarget} />
      )}
    </Page>
  );
}

function StatisticsWizard({ target }: { target: string }) {
  const config = useOnboardingConfig();
  const env = targetOf(config, target);
  const { signedIn } = useTargets();
  const session = signedIn(target);
  const secrets = useSecrets();
  const [org, setOrg] = useWizardParam('org');
  const [customer, setCustomer] = useWizardParam('customer');
  const [step] = useWizardParam('step');
  const [creating, setCreating] = useState(false);
  const customerId = Number(org) || 0;

  const orgs = useAsync(() => stats.organizations({ limit: 500 }), []);
  const state = useAsync(async () => {
    if (!customerId) return null;
    const [detail, installations] = await Promise.all([
      stats.organization(customerId),
      stats.installations({ customerId }),
    ]);
    const owner = session ? await aliasOwner(target, 'pms', String(customerId)) : null;
    const customers = session && !owner ? await allCustomers(target) : [];
    return { detail, installations, owner, customers };
  }, [customerId, session, target]);
  const action = useAction(async () => {
    await Promise.all([orgs.reload(), state.reload()]);
  });
  const s = state.data;
  const d = s?.detail;
  const mode: AuthMode = d?.auth?.mode ?? d?.authMode ?? 'legacy';
  const token = secrets(`stats:${customerId}:token`);
  const receiving = (s?.installations ?? []).filter((i) => i.status === 'ok');
  useInterval(() => void state.reload(), 20_000, step === 'data' && !!customerId && !receiving.length);

  const steps: WizardStep[] = [
    {
      id: 'organization',
      title: 'Organization',
      state: d ? 'done' : 'todo',
      summary: d ? `${d.name ?? ''} (${d.customerId})` : undefined,
      body: (
        <>
          <p>
            The datacollector keys a customer by its PMS customer id. Pick the organization, or create it (the
            hub suggests the next free id).
          </p>
          <Select
            label="Organization"
            value={org}
            onChange={setOrg}
            searchable
            options={[
              { value: '', label: '— pick —' },
              ...(orgs.data?.items ?? []).map((o) => ({
                value: String(o.customerId),
                label: `${o.name ?? '(no name)'} · ${o.customerId}`,
              })),
            ]}
          />
          <StepActions>
            <Button variant="ghost" onClick={() => setCreating(true)}>
              New organization
            </Button>
            {d && (
              <Link className="nyk-link" to={orgPath(d.customerId, 'monitoring')}>
                Monitoring settings
              </Link>
            )}
          </StepActions>
          <NewOrganizationDialog
            open={creating}
            onClose={() => setCreating(false)}
            onCreated={(o) => {
              setCreating(false);
              setOrg(String(o.customerId));
              void orgs.reload();
            }}
          />
        </>
      ),
    },
    {
      id: 'auth',
      title: 'Link to the AUTH customer',
      state: !d ? 'blocked' : s?.owner ? 'done' : 'todo',
      blocked: 'Pick the organization first.',
      summary: s?.owner ? s.owner.customerName : undefined,
      body: (
        <NeedsSession target={target}>
          <p>
            The alias <code>pms:{customerId || '<id>'}</code> on the AUTH customer ties the organization to
            it: its installations then sign in with the customer's AUTH clients and show up under the
            customer.
          </p>
          {s?.owner ? (
            <Facts
              rows={[
                ['AUTH customer', s.owner.customerName],
                ...(s.owner.tenantName ? [['Tenant', s.owner.tenantName] as [string, string]] : []),
              ]}
            />
          ) : (
            <NeedsScope scope={SCOPES.authWrite}>
              <Select
                label="AUTH customer"
                value={customer}
                onChange={setCustomer}
                searchable
                options={[
                  { value: '', label: '— pick —' },
                  ...(s?.customers ?? []).map((c) => ({ value: c.id, label: c.name })),
                ]}
              />
              <StepActions error={action.error}>
                <Button
                  disabled={!customer}
                  loading={action.busy === 'alias'}
                  onClick={() =>
                    void action.run('alias', () =>
                      addAlias(target, 'customers', customer, 'pms', String(customerId))
                    )
                  }
                >
                  Add alias pms:{customerId}
                </Button>
                <Link className="nyk-link" to="/onboarding/tenant">
                  No customer yet: Customer & tenant wizard
                </Link>
              </StepActions>
            </NeedsScope>
          )}
        </NeedsSession>
      ),
    },
    {
      id: 'credential',
      title: 'Ingest credential',
      state: !d ? 'blocked' : d.token.issued || mode !== 'legacy' ? 'done' : 'todo',
      blocked: 'Pick the organization first.',
      summary: d ? `${d.token.issued ? 'token' : 'no token'} · mode ${mode}` : undefined,
      body: d ? (
        <>
          <p>
            Today's WorkServers send a per-organization token; with AUTH they send their <code>wms</code>{' '}
            client's token (scope <code>datacollector:write</code>, set up in the Customer & tenant wizard).
            Mode <b>both</b> accepts either, <b>auth</b> only AUTH.
          </p>
          <Facts
            rows={[
              [
                'Token',
                d.token.issued
                  ? `issued ${d.token.issuedAt ? formatRelative(d.token.issuedAt) : ''}`
                  : 'none',
              ],
              ['Mode', mode],
            ]}
          />
          {token && <SecretValue label="Ingest token" value={token} />}
          <StepActions error={action.error}>
            <Button
              variant={d.token.issued ? 'ghost' : 'primary'}
              loading={action.busy === 'token'}
              onClick={() =>
                void action.run('token', async () => {
                  const r = await stats.issueToken(customerId);
                  rememberSecret(`stats:${customerId}:token`, r.token);
                })
              }
            >
              {d.token.issued ? 'Issue a new token (replaces the old)' : 'Issue token'}
            </Button>
            {AUTH_MODES.filter((m) => m !== mode).map((m) => (
              <Button
                key={m}
                variant="ghost"
                loading={action.busy === `mode-${m}`}
                onClick={() => void action.run(`mode-${m}`, () => stats.setAuthMode(customerId, m))}
              >
                Mode {m}
              </Button>
            ))}
          </StepActions>
        </>
      ) : null,
    },
    {
      id: 'workserver',
      title: 'WorkServer options',
      state: !d ? 'blocked' : d.token.issued || mode !== 'legacy' ? 'done' : 'blocked',
      blocked: 'Set up the ingest credential first.',
      body: env ? (
        <>
          {!env.collectorUrl && (
            <Note tone="warn">
              The collector URL is not configured (<code>Onboarding:Targets:{target}:CollectorUrl</code>):
              fill in <code>Uri</code> by hand.
            </Note>
          )}
          <FileOutput
            files={workServerFiles({
              authServiceUrl: env.authServiceUrl,
              datacollector: {
                uri: env.collectorUrl ?? 'TODO-collector-URL',
                token: mode === 'auth' ? undefined : (token ?? (d?.token.issued ? PLACEHOLDER : undefined)),
                auth: mode === 'legacy' ? 'Off' : mode === 'both' ? 'Both' : 'Bearer',
              },
            })}
          />
          <p className="nyk-ui-hint">Merge into the WorkServer options and restart the WorkServer.</p>
        </>
      ) : null,
    },
    {
      id: 'data',
      title: 'First data',
      state: !d ? 'blocked' : receiving.length ? 'done' : 'waiting',
      blocked: 'Pick the organization first.',
      summary: s?.installations.length ? `${s.installations.length} installation(s)` : undefined,
      body: (
        <>
          {s?.installations.length ? (
            <Facts
              rows={s.installations.map((i) => [
                i.workserver,
                `${i.status}${i.lastSeen.performance ? ` · performance ${formatRelative(i.lastSeen.performance)}` : ''}${i.lastSeen.log ? ` · log ${formatRelative(i.lastSeen.log)}` : ''}`,
              ])}
            />
          ) : null}
          {receiving.length ? (
            <Note tone="good">Data is arriving from {receiving.map((i) => i.workserver).join(', ')}.</Note>
          ) : (
            <Note tone="warn">
              Waiting for the first data (checked every 20 s). Performance data arrives within minutes of a
              restart.
            </Note>
          )}
        </>
      ),
    },
  ];

  return (
    <Wizard
      steps={steps}
      loading={orgs.loading && !orgs.data}
      error={orgs.error ?? state.error}
      onRecheck={() => void action.run('recheck', async () => undefined)}
    />
  );
}
